import React from 'react';
import { JsonItem, JsonNode, JsonTokenType, JsonValue } from './types';

/**
 * Размер отступа для одного уровня вложенности (количество пробелов).
 */
export const INDENT_SIZE = 2;

/**
 * Строит дерево узлов из произвольного JSON-значения.
 * Для примитивов возвращает листовой `JsonItem`, для контейнеров — `JsonNode`.
 */
export const buildTree = (value: JsonValue, depth: number, path: string, key?: string): JsonNode | JsonItem => {
  if (value === null || typeof value !== 'object') {
    const tokenType: JsonTokenType = value === null ? 'null' : (typeof value as JsonTokenType);
    const displayValue = typeof value === 'string' ? JSON.stringify(value) : String(value);
    return { kind: 'leaf', key, value: displayValue, tokenType, isLast: true };
  }

  if (Array.isArray(value)) {
    const items: JsonItem[] = value.map((item, index) => {
      const childPath = `${path}[${index}]`;
      const result = buildTree(item, depth + 1, childPath);
      if ('kind' in result) {
        return { ...result, isLast: index === value.length - 1 };
      }
      return { kind: 'node' as const, node: result, isLast: index === value.length - 1 };
    });

    return {
      type: 'array',
      key,
      children: items
        .filter((i): i is { kind: 'node'; node: JsonNode; isLast: boolean } => i.kind === 'node')
        .map((i) => i.node),
      entries: items.filter((i): i is JsonItem & { kind: 'leaf' } => i.kind === 'leaf'),
      items,
      depth,
      path,
    };
  }

  const entries = Object.entries(value);
  const items: JsonItem[] = entries.map(([k, v], index) => {
    const childPath = `${path}.${k}`;
    const result = buildTree(v as JsonValue, depth + 1, childPath, k);
    if ('kind' in result) {
      return { ...result, key: k, isLast: index === entries.length - 1 };
    }
    return { kind: 'node' as const, node: { ...result, key: k }, isLast: index === entries.length - 1 };
  });

  return {
    type: 'object',
    key,
    children: items
      .filter((i): i is { kind: 'node'; node: JsonNode; isLast: boolean } => i.kind === 'node')
      .map((i) => i.node),
    entries: items.filter((i): i is JsonItem & { kind: 'leaf' } => i.kind === 'leaf'),
    items,
    depth,
    path,
  };
};

/**
 * Собирает пути всех вложенных узлов дерева — используется для управления массовым сворачиванием.
 */
export const collectAllPaths = (node: JsonNode): Set<string> => {
  const paths = new Set<string>();
  paths.add(node.path);
  for (const child of node.children) {
    for (const p of collectAllPaths(child)) {
      paths.add(p);
    }
  }
  for (const item of node.items) {
    if (item.kind === 'node') {
      for (const p of collectAllPaths(item.node)) {
        paths.add(p);
      }
    }
  }
  return paths;
};

/**
 * Возвращает CSS-класс синтаксической подсветки для типа токена.
 */
export const getTokenClassName = (type: JsonTokenType | 'key'): string => {
  switch (type) {
    case 'key':
      return 'text-sky-600 dark:text-sky-400';
    case 'string':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'number':
      return 'text-amber-600 dark:text-amber-400';
    case 'boolean':
      return 'text-purple-600 dark:text-purple-400';
    case 'null':
      return 'text-rose-600 dark:text-rose-400';
    default:
      return 'text-muted-foreground';
  }
};

/**
 * Подсвечивает совпадения подстроки `searchQuery` в `text` обёрткой `<mark>`.
 * Регистронезависимый поиск; пустой запрос возвращает текст без изменений.
 */
export const highlightText = (text: string, searchQuery: string): React.ReactNode => {
  if (!searchQuery) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = searchQuery.toLowerCase();
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let matchIndex = lowerText.indexOf(lowerQuery, lastIndex);

  while (matchIndex !== -1) {
    if (matchIndex > lastIndex) {
      parts.push(text.slice(lastIndex, matchIndex));
    }
    parts.push(
      <mark key={matchIndex} className="bg-yellow-300 dark:bg-yellow-600 rounded-sm text-inherit">
        {text.slice(matchIndex, matchIndex + searchQuery.length)}
      </mark>,
    );
    lastIndex = matchIndex + searchQuery.length;
    matchIndex = lowerText.indexOf(lowerQuery, lastIndex);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};
